import { cn } from "lib/utils";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState } from "react";

interface HexagonImageProps {
  imageSize?: number;
  imageUrl: string;
  borderColor?: string;
  borderWidth?: number;
  className?: string;
  alt?: string;
  boxShadowColor?: string;
  boxShadowWidth?: number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isSquare?: boolean;
  isCircle?: boolean;
}

export const HexagonImage = ({
  imageSize = 200,
  imageUrl,
  borderColor,
  borderWidth = 2,
  className,
  alt,
  boxShadowColor,
  boxShadowWidth = 5,
  onClick,
  isSquare = false,
  isCircle = false,
}: HexagonImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // If borderColor is provided (not undefined or null), show the border
  const showBorder = borderColor !== undefined && borderColor !== null;

  // Use the provided borderColor, or default to blue if an empty string is provided
  const actualBorderColor = borderColor === "" ? "#0070f3" : borderColor;
  const loadingBorderColor = showBorder ? actualBorderColor : "#1581ed";

  return (
    <div
      className={cn("relative", className)}
      onClick={onClick}
      style={{
        width: `${imageSize}px`,
        height: `${imageSize}px`,
        ...(boxShadowColor && {
          filter: `drop-shadow(0 0 ${boxShadowWidth}px ${boxShadowColor})`,
        }),
      }}
    >
      {/* Border hexagon (only shown when borderColor is provided) */}
      {showBorder && (
        <div
          className="absolute inset-0"
          style={{
            ...(isCircle
              ? { borderRadius: "50%" }
              : {
                  clipPath: isSquare
                    ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    : "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                }),
            width: `${imageSize}px`,
            height: `${imageSize}px`,
            backgroundColor: actualBorderColor,
          }}
        />
      )}

      {/* Inner container (hexagon or square based on isSquare prop) */}
      <div
        className="absolute overflow-hidden flex items-center justify-center"
        style={{
          ...(isCircle
            ? { borderRadius: "50%" }
            : {
                clipPath: isSquare
                  ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                  : "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
              }),
          width: showBorder ? `${imageSize - borderWidth * 2}px` : `${imageSize}px`,
          height: showBorder ? `${imageSize - borderWidth * 2}px` : `${imageSize}px`,
          top: showBorder ? `${borderWidth}px` : "0",
          left: showBorder ? `${borderWidth}px` : "0",
        }}
      >
        {/* Loading state with border */}
        {!isLoaded && (
          <div className="absolute inset-0 z-10">
            {/* Loading border (hexagon or square based on isSquare prop) */}
            <div
              className="absolute inset-0"
              style={{
                ...(isCircle
                  ? { borderRadius: "50%" }
                  : {
                      clipPath: isSquare
                        ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                        : "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    }),
                backgroundColor: loadingBorderColor,
              }}
            />

            {/* Loading inner container (hexagon or square based on isSquare prop) */}
            <div
              className="absolute flex items-center justify-center bg-gray-300"
              style={{
                ...(isCircle
                  ? { borderRadius: "50%" }
                  : {
                      clipPath: isSquare
                        ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                        : "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    }),
                top: `${borderWidth}px`,
                left: `${borderWidth}px`,
                right: `${borderWidth}px`,
                bottom: `${borderWidth}px`,
                width: `calc(100% - ${borderWidth * 2}px)`,
                height: `calc(100% - ${borderWidth * 2}px)`,
              }}
            >
              <ProgressSpinner
                style={{
                  width: `${imageSize - imageSize / 2}px`,
                  height: `${imageSize - imageSize / 2}px`,
                }}
              />
            </div>
          </div>
        )}

        <img
          src={imageUrl}
          alt={alt}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setIsLoaded(true)}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            // imageRendering: "crisp-edges",
            // backfaceVisibility: "hidden",
            // WebkitFontSmoothing: "antialiased",
          }}
        />
      </div>
    </div>
  );
};
